﻿import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type FormEvent,
} from 'react';

import { ApiError } from './api';
import { App } from './App';
import { createAdminApi, type AdminApi } from './admin-api';
import { getAdminAuthCoordinator, type AdminAuthCoordinator } from './admin-auth-coordinator';
import { problemMessage } from './admin-presentation';
import { isRestrictedAdmin, isSystemAdmin, type AdminSession } from './admin-session';
import { TemplatesAdmin } from './a3-admin';
import { AuditAdmin, EmailPolicyAdmin, OpsDashboard } from './a4-admin';
import { ConfigurationCatalogAdmin } from './configuration-catalog-admin';
import { ContractorAdmin, UsersAdmin, VehicleAdmin } from './administration-master';
import { confirmAdminNavigation } from './admin-leave-guard';
import { ContractorContinueApp } from './contractor-continue';
import { ContractorRevalidationApp } from './contractor-revalidation';
import { NativeBookingArea } from './native-booking-area';
import { ArchiveAdmin, InspectionsAdmin } from './operational-admin';
import { NativeSiteSchedulesAdmin } from './native-site-schedules-admin';
import {
  isAdminLoginPath,
  isAdminPath,
  isContinuePath,
  isNativeCabinetPath,
  isPrefillPath,
  isRevalidationPath,
  navigate,
  readPortalPath,
  subscribePortalPath,
} from './path-router';
import { applyUiModeToDocument, clearUiModeFromDocument } from './ui-mode';

const ENABLED_ADMIN_PATHS = new Set([
  '/admin',
  '/admin/inspections',
  '/admin/archive',
  '/admin/users',
  '/admin/contractors',
  '/admin/vehicles',
  '/admin/templates',
  '/admin/reference-data',
  '/admin/site-schedules',
  '/admin/email',
  '/admin/audit',
]);

/** Retired Admin config UI; direct URL redirects to /admin (Package E). */
const RETIRED_ADMIN_PATHS = new Set(['/admin/rubitime']);

const ADMIN_NAV: readonly { readonly path: string; readonly label: string }[] = [
  { path: '/admin', label: 'Операции' },
  { path: '/admin/inspections', label: 'Осмотры' },
  { path: '/admin/archive', label: 'Архив' },
  { path: '/admin/contractors', label: 'Подрядчики' },
  { path: '/admin/vehicles', label: 'ТС и оборудование' },
  { path: '/admin/users', label: 'Пользователи' },
  { path: '/admin/templates', label: 'Чек-листы' },
  { path: '/admin/reference-data', label: 'Справочники' },
  { path: '/admin/site-schedules', label: 'Расписание площадок' },
  { path: '/admin/email', label: 'Рассылка результатов' },
  { path: '/admin/audit', label: 'Аудит' },
];

function isNavActive(itemPath: string, path: string): boolean {
  if (itemPath === '/admin') {
    return path === '/admin';
  }
  return path === itemPath || path.startsWith(`${itemPath}/`);
}

function usePortalPath(): string {
  return useSyncExternalStore(subscribePortalPath, readPortalPath, readPortalPath);
}

function useAdminUiMode(): void {
  useLayoutEffect(() => {
    document.body.classList.add('admin-app');
    applyUiModeToDocument('comfortable');
    return () => {
      document.body.classList.remove('admin-app', 'sidebar-open');
      clearUiModeFromDocument();
    };
  }, []);
}

function adminBreadcrumbs(
  path: string,
): readonly { readonly label: string; readonly current?: boolean }[] {
  const crumbs: { readonly label: string; readonly current?: boolean }[] = [
    { label: 'Администрирование' },
  ];
  const nav = ADMIN_NAV.find((item) => isNavActive(item.path, path));
  if (nav !== undefined) {
    crumbs.push({ label: nav.label, current: path === nav.path || !path.includes('/', 7) });
  }
  if (path.startsWith('/admin/inspections/') && path.length > '/admin/inspections/'.length) {
    crumbs.push({ label: 'Карточка осмотра', current: true });
  } else if (path.startsWith('/admin/archive/') && path.length > '/admin/archive/'.length) {
    crumbs.push({ label: 'Архивная карточка', current: true });
  } else if (path === '/admin/users/new') {
    crumbs.push({ label: 'Новый пользователь', current: true });
  } else if (
    path === '/admin/users/trusted-emails' ||
    path.startsWith('/admin/users/trusted-emails/')
  ) {
    crumbs.push({ label: 'Доверенные адреса подрядчиков', current: true });
  } else if (path.startsWith('/admin/users/') && path.length > '/admin/users/'.length) {
    crumbs.push({ label: 'Пользователь', current: true });
  } else if (path === '/admin/contractors/new') {
    crumbs.push({ label: 'Новый подрядчик', current: true });
  } else if (path.startsWith('/admin/contractors/') && path.length > '/admin/contractors/'.length) {
    crumbs.push({ label: 'Подрядчик', current: true });
  } else if (path === '/admin/vehicles/new') {
    crumbs.push({ label: 'Новое ТС', current: true });
  } else if (path.startsWith('/admin/vehicles/') && path.length > '/admin/vehicles/'.length) {
    crumbs.push({ label: 'ТС', current: true });
  } else if (nav !== undefined) {
    return [{ label: 'Администрирование' }, { label: nav.label, current: true }];
  }
  return crumbs;
}

export function Portal(props: { readonly apiBaseUrl: string }) {
  const path = usePortalPath();

  if (isAdminPath(path)) {
    return <AdminArea apiBaseUrl={props.apiBaseUrl} path={path} />;
  }

  if (path === '/' || isNativeCabinetPath(path)) {
    return <NativeBookingArea apiBaseUrl={props.apiBaseUrl} path={path === '/' ? '/booking' : path} />;
  }

  if (isRevalidationPath(path)) {
    return <ContractorRevalidationApp apiBaseUrl={props.apiBaseUrl} />;
  }

  if (isContinuePath(path)) {
    return <ContractorContinueApp apiBaseUrl={props.apiBaseUrl} />;
  }

  if (isPrefillPath(path)) {
    return <App apiBaseUrl={props.apiBaseUrl} />;
  }

  return (
    <main className="page">
      <h1>Допуск ТС</h1>
      <p>Страница не найдена.</p>
    </main>
  );
}

function AdminArea(props: { readonly apiBaseUrl: string; readonly path: string }) {
  const api = useMemo(() => createAdminApi(props.apiBaseUrl), [props.apiBaseUrl]);
  const auth = useMemo(() => {
    const coordinator = getAdminAuthCoordinator(api);
    api.setAccessTokenRecoverer((failedAccessToken) =>
      coordinator.recoverExpiredAccessToken(failedAccessToken),
    );
    return coordinator;
  }, [api]);
  const snapshot = useSyncExternalStore(auth.subscribe, auth.getSnapshot, auth.getSnapshot);
  const session = snapshot.session;
  const [passwordChanged, setPasswordChanged] = useState(false);
  useAdminUiMode();

  useEffect(() => {
    void auth.bootstrap();
  }, [auth]);

  useLayoutEffect(() => {
    if (passwordChanged || snapshot.phase === 'BOOTSTRAPPING') {
      return;
    }
    if (session === null && !isAdminLoginPath(props.path)) {
      window.history.replaceState({}, '', '/admin/login');
      return;
    }
    if (
      isAdminLoginPath(props.path) &&
      session !== null &&
      isSystemAdmin(session.user) &&
      !isRestrictedAdmin(session)
    ) {
      navigate('/admin', 'replace');
    }
  }, [props.path, session, passwordChanged, snapshot.phase]);

  if (passwordChanged) {
    return (
      <main className="login-screen">
        <div className="login-card">
          <h1>Допуск ТС</h1>
          <p role="status" className="banner banner-info">
            Пароль изменён. Войдите заново. Предыдущий ограниченный сеанс больше не действует.
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setPasswordChanged(false);
              navigate('/admin/login', 'replace');
            }}
          >
            К входу
          </button>
        </div>
      </main>
    );
  }

  if (snapshot.phase === 'BOOTSTRAPPING') {
    return (
      <main className="login-screen">
        <div className="login-card">
          <h1>Допуск ТС</h1>
          <p role="status">Проверка сеанса…</p>
        </div>
      </main>
    );
  }

  if (session === null) {
    return <AdminLogin auth={auth} bootstrapError={snapshot.bootstrapError} />;
  }

  if (isRestrictedAdmin(session)) {
    return (
      <RestrictedAdminState
        session={session}
        api={api}
        auth={auth}
        logoutError={snapshot.logoutError}
        onPasswordChanged={() => {
          setPasswordChanged(true);
        }}
      />
    );
  }

  if (!isSystemAdmin(session.user)) {
    return (
      <ForbiddenAdminState
        session={session}
        api={api}
        auth={auth}
        logoutError={snapshot.logoutError}
      />
    );
  }

  if (isAdminLoginPath(props.path)) {
    return (
      <main className="login-screen">
        <div className="login-card">
          <p>Загрузка…</p>
        </div>
      </main>
    );
  }

  return (
    <AdminShell
      apiBaseUrl={props.apiBaseUrl}
      session={session}
      path={props.path}
      api={api}
      auth={auth}
      logoutError={snapshot.logoutError}
    />
  );
}

function AdminLogin(props: {
  readonly auth: AdminAuthCoordinator;
  readonly bootstrapError: string | null;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await props.auth.login(username, password);
      if (isRestrictedAdmin(session)) {
        navigate('/admin', 'replace');
        return;
      }
      if (!isSystemAdmin(session.user)) {
        navigate('/admin', 'replace');
        return;
      }
      navigate('/admin', 'replace');
    } catch (failure) {
      setError(loginErrorCopy(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-screen">
      <div className="login-card">
        <h1>Допуск ТС</h1>
        <p className="login-context">Администрирование · CheckTS / GIRYA</p>
        <form onSubmit={(event) => void submit(event)}>
          <label htmlFor="admin_username">
            Имя пользователя
            <input
              id="admin_username"
              name="username"
              autoComplete="username"
              value={username}
              disabled={busy}
              onChange={(event) => {
                setUsername(event.target.value);
              }}
            />
          </label>
          <label htmlFor="admin_password">
            Пароль
            <input
              id="admin_password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              disabled={busy}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </label>
          {props.bootstrapError !== null ? (
            <p role="alert" className="banner banner-closed">
              {props.bootstrapError}
            </p>
          ) : null}
          {error !== null ? (
            <p role="alert" className="banner banner-closed">
              {error}
            </p>
          ) : null}
          <div className="actions">
            <button type="submit" className="btn-primary" disabled={busy}>
              Войти
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

function AdminShell(props: {
  readonly apiBaseUrl: string;
  readonly session: AdminSession;
  readonly path: string;
  readonly api: AdminApi;
  readonly auth: AdminAuthCoordinator;
  readonly logoutError: string | null;
}) {
  const known =
    ADMIN_NAV.some((item) => item.path === props.path) ||
    isOperationalPath(props.path) ||
    RETIRED_ADMIN_PATHS.has(props.path);
  const [navOpen, setNavOpen] = useState(false);
  const crumbs = adminBreadcrumbs(props.path);

  useLayoutEffect(() => {
    if (RETIRED_ADMIN_PATHS.has(props.path)) {
      navigate('/admin', 'replace');
    }
  }, [props.path]);

  useEffect(() => {
    document.body.classList.toggle('sidebar-open', navOpen);
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [navOpen]);

  useEffect(() => {
    setNavOpen(false);
  }, [props.path]);

  const closeNav = () => {
    setNavOpen(false);
  };

  return (
    <div className="shell app-shell">
      <button
        type="button"
        className="sidebar-backdrop"
        aria-label="Закрыть меню"
        onClick={closeNav}
      />
      <nav className="shell-nav sidebar" id="admin-sidebar" aria-label="Администрирование">
        <div className="sidebar-brand">
          <p className="shell-brand brand-name">Допуск ТС</p>
          <span className="brand-badge">Admin</span>
        </div>
        <div className="sidebar-nav">
          <ul className="shell-menu">
            {ADMIN_NAV.map((item) => (
              <li key={item.path}>
                {ENABLED_ADMIN_PATHS.has(item.path) ? (
                  <button
                    type="button"
                    className={
                      isNavActive(item.path, props.path)
                        ? 'nav-link nav-current is-active'
                        : 'nav-link'
                    }
                    aria-current={isNavActive(item.path, props.path) ? 'page' : undefined}
                    onClick={() => {
                      if (!confirmAdminNavigation()) {
                        return;
                      }
                      closeNav();
                      navigate(item.path);
                    }}
                  >
                    {item.label}
                  </button>
                ) : (
                  <span className="nav-disabled">
                    {item.label}
                    <span className="nav-note"> ещё не реализовано</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="sidebar-user">
          <span className="user-name">{props.session.user.display_name}</span>
          <span className="user-login">SYSTEM_ADMIN</span>
          <button
            type="button"
            className="btn-ghost btn-logout"
            onClick={() => {
              void logout(props.auth);
            }}
          >
            Выйти
          </button>
          {props.logoutError !== null ? (
            <p role="alert" className="banner banner-closed">
              {props.logoutError}
            </p>
          ) : null}
        </div>
      </nav>
      <div className="main-area">
        <div className="top-bar">
          <button
            type="button"
            className="sidebar-toggle"
            aria-expanded={navOpen}
            aria-controls="admin-sidebar"
            onClick={() => {
              setNavOpen((open) => !open);
            }}
          >
            Меню
          </button>
          <nav className="breadcrumbs" aria-label="Иерархия">
            <ol className="breadcrumb-list">
              {crumbs.map((crumb) => (
                <li
                  key={crumb.label}
                  className={crumb.current === true ? 'breadcrumb-current' : undefined}
                >
                  {crumb.label}
                </li>
              ))}
            </ol>
          </nav>
          <span className="top-bar-user">{props.session.user.display_name}</span>
        </div>
        <main className="page page-wide main-content">
          {props.path === '/admin' ? (
            <OpsDashboard api={props.api} />
          ) : props.path === '/admin/inspections' ||
            props.path.startsWith('/admin/inspections/') ? (
            <InspectionsAdmin api={props.api} path={props.path} />
          ) : props.path === '/admin/archive' || props.path.startsWith('/admin/archive/') ? (
            <ArchiveAdmin api={props.api} path={props.path} />
          ) : props.path === '/admin/users' || props.path.startsWith('/admin/users/') ? (
            <UsersAdmin api={props.api} path={props.path} />
          ) : props.path === '/admin/contractors' ||
            props.path.startsWith('/admin/contractors/') ? (
            <ContractorAdmin api={props.api} path={props.path} />
          ) : props.path === '/admin/vehicles' || props.path.startsWith('/admin/vehicles/') ? (
            <VehicleAdmin api={props.api} path={props.path} />
          ) : props.path === '/admin/templates' || props.path.startsWith('/admin/templates/') ? (
            <TemplatesAdmin api={props.api} />
          ) : props.path === '/admin/reference-data' ||
            props.path.startsWith('/admin/reference-data/') ? (
            <ConfigurationCatalogAdmin api={props.api} path={props.path} />
          ) : props.path === '/admin/site-schedules' ? (
            <NativeSiteSchedulesAdmin
              apiBaseUrl={props.apiBaseUrl}
              adminApi={props.api}
              accessToken={props.session.accessToken}
            />
          ) : props.path === '/admin/email' ? (
            <EmailPolicyAdmin api={props.api} />
          ) : RETIRED_ADMIN_PATHS.has(props.path) ? (
            <p role="status">Перенаправление…</p>
          ) : props.path === '/admin/audit' ? (
            <AuditAdmin api={props.api} />
          ) : known ? (
            <>
              <h1>Администрирование</h1>
              <p role="status" className="banner">
                Раздел ещё не реализован. Бизнес-данные A3–A4 здесь не отображаются.
              </p>
            </>
          ) : (
            <>
              <h1>Администрирование</h1>
              <p role="status" className="banner">
                Раздел администрирования не найден.
              </p>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function isOperationalPath(path: string): boolean {
  return (
    path.startsWith('/admin/users/') ||
    path.startsWith('/admin/contractors/') ||
    path.startsWith('/admin/vehicles/') ||
    path.startsWith('/admin/templates/') ||
    path.startsWith('/admin/inspections/') ||
    path.startsWith('/admin/archive/')
  );
}

function ForbiddenAdminState(props: {
  readonly session: AdminSession;
  readonly api: ReturnType<typeof createAdminApi>;
  readonly auth: AdminAuthCoordinator;
  readonly logoutError: string | null;
}) {
  return (
    <main className="login-screen">
      <div className="login-card">
        <h1>Допуск ТС</h1>
        <p className="login-context">Администрирование</p>
        <p role="alert" className="banner banner-closed">
          Недостаточно прав. Требуется роль SYSTEM_ADMIN.
        </p>
        <p className="meta">{props.session.user.display_name}</p>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => {
            void logout(props.auth);
          }}
        >
          Выйти
        </button>
        {props.logoutError !== null ? (
          <p role="alert" className="banner banner-closed">
            {props.logoutError}
          </p>
        ) : null}
      </div>
    </main>
  );
}

function RestrictedAdminState(props: {
  readonly session: AdminSession;
  readonly api: AdminApi;
  readonly auth: AdminAuthCoordinator;
  readonly logoutError: string | null;
  readonly onPasswordChanged: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Новый пароль и подтверждение не совпадают.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await props.api.changeOwnPassword(currentPassword, newPassword);
      await props.auth.logout();
      props.onPasswordChanged();
    } catch (failure) {
      setError(problemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-screen">
      <div className="login-card">
        <h1>Допуск ТС</h1>
        <p className="login-context">Смена временного пароля</p>
        <p role="status" className="banner banner-info">
          Требуется смена временного пароля. Административные разделы недоступны, пока пароль не
          изменён.
        </p>
        <form onSubmit={(event) => void submit(event)}>
          <label htmlFor="current_password">
            Текущий временный пароль
            <input
              id="current_password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              disabled={busy}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </label>
          <label htmlFor="new_password">
            Новый пароль
            <input
              id="new_password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              disabled={busy}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </label>
          <label htmlFor="confirm_password">
            Подтверждение
            <input
              id="confirm_password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              disabled={busy}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </label>
          {error !== null ? (
            <p role="alert" className="banner banner-closed">
              {error}
            </p>
          ) : null}
          <div className="actions">
            <button type="submit" className="btn-primary" disabled={busy}>
              Сменить пароль
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                void logout(props.auth);
              }}
            >
              Выйти
            </button>
          </div>
          {props.logoutError !== null ? (
            <p role="alert" className="banner banner-closed">
              {props.logoutError}
            </p>
          ) : null}
        </form>
      </div>
    </main>
  );
}

async function logout(auth: AdminAuthCoordinator): Promise<void> {
  try {
    await auth.logout();
  } catch {
    return;
  }
  navigate('/admin/login', 'replace');
}

function loginErrorCopy(failure: unknown): string {
  if (failure instanceof ApiError && failure.problem.code === 'UNAUTHENTICATED') {
    return 'Неверные учётные данные.';
  }
  return 'Не удалось выполнить вход.';
}
